import {useForm, type UseFormProps} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {getCategoryCollisionError} from '../errors';

import {z} from 'zod';
import {useEffect} from 'react';

const CategoryCollisionFormValuesSchema = z.object({
	skipped: z.boolean(),
});

export type ICategoryCollisionFormValues = z.infer<
	typeof CategoryCollisionFormValuesSchema
>;

/** Default values for the form */
export const defaultCategoryCollisionFormValues: ICategoryCollisionFormValues =
	{
		skipped: false,
	};

export interface UseCategoryCollisionFormParams
	extends UseFormProps<ICategoryCollisionFormValues> {
	initialValues?: Partial<ICategoryCollisionFormValues>;
	submit: (data: ICategoryCollisionFormValues) => Promise<void>;
}

/**
 * react-hook-form for merging vocabulary values
 */
export const useCategoryCollisionForm = ({
	initialValues = defaultCategoryCollisionFormValues,
	submit,
	...props
}: UseCategoryCollisionFormParams) => {
	const form = useForm<ICategoryCollisionFormValues>({
		mode: 'all',
		criteriaMode: 'all',
		defaultValues: initialValues,
		resolver: zodResolver(CategoryCollisionFormValuesSchema),
		...props,
	});

	useEffect(() => {
		form.register('skipped');
		return () => {
			form.unregister('skipped');
		};
	}, [form]);

	/** Reset the form to the default values */
	const reset = useConstCallback(
		(values?: Partial<ICategoryCollisionFormValues>) =>
			form.reset({
				...defaultCategoryCollisionFormValues,
				...values,
			}),
	);

	/** Form submission handler */
	const onSubmit = form.handleSubmit(async (data) => {
		return submit(data).catch((error) => {
			form.setError('root', {
				message: getCategoryCollisionError('update', error.code),
				type: 'error',
			});
		});
	});

	return {
		form,
		onSubmit,
		reset,
	};
};
